import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../tools';

import type { SelectorOption } from '../common';
import { PRIORITY_OPTIONS } from '../../constants';

interface UseCompositeParams {
    targetDocumentUrl: string;
    components: any;
    priority: SelectorOption;
    surfaceSpecificationUrl: string;
    surfaceSpecification: any;
    applyOnlyToPlaceholders: boolean;
    fillDefaultText: boolean;
    smartCrop: boolean;
    isBetaEndpoint: boolean;
    brandProfileUrl: string;
    enableMulticolorLogoContrastFix: boolean;
    enableMultiColorLogoToMonochromeConversion: boolean;
    convertSolidColorImagesToShapes: boolean;
    setTargetDocumentUrl: (url: string) => void;
    setComponents: (components: any) => void;
    setPriority: (priority: SelectorOption) => void;
    setSurfaceSpecificationUrl: (url: string) => void;
    setSurfaceSpecification: (surface: any) => void;
    setApplyOnlyToPlaceholders: (applyOnlyToPlaceholders: boolean) => void;
    setSmartCrop: (smartCrop: boolean) => void;
    setFillDefaultText: (fillDefaultText: boolean) => void;
    setIsBetaEndpoint: (isBetaEndpoint: boolean) => void;
    setBrandProfileUrl: (url: string) => void;
    setEnableMulticolorLogoContrastFix: (flag: boolean) => void;
    setEnableMultiColorLogoToMonochromeConversion: (flag: boolean) => void;
    setConvertSolidColorImagesToShapes: (flag: boolean) => void;
}

export const useCompositeParams = (): UseCompositeParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();

    const documentUrl = searchParams.get('targetDocumentUrl');
    const componentsParam = searchParams.get('components');
    const priorityParam = searchParams.get('priority') || 'quality';
    const surfaceSpecificationParam = searchParams.get('surfaceSpecification');
    const surfaceSpecificationUrlParam = searchParams.get('surfaceSpecificationUrl');
    const applyOnlyToPlaceholdersParam = searchParams.get('applyOnlyToPlaceholders');
    const fillDefaultTextParam = searchParams.get('fillDefaultText');
    const isBetaEndpointParam = searchParams.get('isBetaEndpoint');
    const useSmartCropParam = searchParams.get('useSmartCrop');
    const brandProfileUrlParam = searchParams.get('brandProfileUrl');
    const enableMulticolorLogoContrastFixParam = searchParams.get('enableMulticolorLogoContrastFix');
    const enableMultiColorLogoToMonochromeConversionParam = searchParams.get(
        'enableMultiColorLogoToMonochromeConversion',
    );

    const convertSolidColorImagesToShapesParam = searchParams.get('convertSolidColorImagesToShapes');

    const [targetDocumentUrl, setTargetDocumentUrl] = useState(documentUrl || '');
    const [components, setComponents] = useState(componentsParam ? JSON.parse(componentsParam) : undefined);
    const [priority, setPriority] = useState<SelectorOption>(
        priorityParam ? { label: priorityParam, value: priorityParam } : PRIORITY_OPTIONS[0],
    );
    const [surfaceSpecificationUrl, setSurfaceSpecificationUrl] = useState(surfaceSpecificationUrlParam || '');
    const [surfaceSpecification, setSurfaceSpecification] = useState(
        surfaceSpecificationParam ? JSON.parse(surfaceSpecificationParam) : undefined,
    );
    const [applyOnlyToPlaceholders, setApplyOnlyToPlaceholders] = useState(!!applyOnlyToPlaceholdersParam || false);
    const [fillDefaultText, setFillDefaultText] = useState(!!fillDefaultTextParam || false);
    const [isBetaEndpoint, setIsBetaEndpoint] = useState(!!isBetaEndpointParam || false);
    const [smartCrop, setSmartCrop] = useState(!!useSmartCropParam || false);
    const [brandProfileUrl, setBrandProfileUrl] = useState(brandProfileUrlParam || '');
    const [enableMulticolorLogoContrastFix, setEnableMulticolorLogoContrastFix] = useState(
        !!enableMulticolorLogoContrastFixParam || false,
    );
    const [enableMultiColorLogoToMonochromeConversion, setEnableMultiColorLogoToMonochromeConversion] = useState(
        !!enableMultiColorLogoToMonochromeConversionParam || false,
    );
    const [convertSolidColorImagesToShapes, setConvertSolidColorImagesToShapes] = useState(
        !!convertSolidColorImagesToShapesParam || false,
    );

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string> = {};

            if (targetDocumentUrl) {
                searchParamsObj['targetDocumentUrl'] = targetDocumentUrl;
            }

            if (components) {
                searchParamsObj['components'] = JSON.stringify(components);
            }

            if (priority) {
                searchParamsObj['priority'] = priority.value;
            }

            if (surfaceSpecificationUrl) {
                searchParamsObj['surfaceSpecificationUrl'] = surfaceSpecificationUrl;
            }

            if (surfaceSpecification) {
                searchParamsObj['surfaceSpecification'] = JSON.stringify(surfaceSpecification);
            }

            if (applyOnlyToPlaceholders) {
                searchParamsObj['applyOnlyToPlaceholders'] = applyOnlyToPlaceholders.toString();
            }

            if (smartCrop) {
                searchParamsObj['smartCrop'] = smartCrop.toString();
            }

            if (fillDefaultText) {
                searchParamsObj['fillDefaultText'] = fillDefaultText.toString();
            }

            if (isBetaEndpoint) {
                searchParamsObj['isBetaEndpoint'] = isBetaEndpoint.toString();
            }

            if (brandProfileUrl) {
                searchParamsObj['brandProfileUrl'] = brandProfileUrl;
            }

            if (enableMulticolorLogoContrastFix) {
                searchParamsObj['enableMulticolorLogoContrastFix'] = enableMulticolorLogoContrastFix.toString();
            }

            if (enableMultiColorLogoToMonochromeConversion) {
                searchParamsObj['enableMultiColorLogoToMonochromeConversion'] =
                    enableMultiColorLogoToMonochromeConversion.toString();
            }

            if (convertSolidColorImagesToShapes) {
                searchParamsObj['convertSolidColorImagesToShapes'] = convertSolidColorImagesToShapes.toString();
            }
            setSearchParams(searchParamsObj);
        },
        [
            targetDocumentUrl,
            components,
            priority,
            setSearchParams,
            surfaceSpecificationUrl,
            surfaceSpecification,
            applyOnlyToPlaceholders,
            fillDefaultText,
            smartCrop,
            isBetaEndpoint,
            brandProfileUrl,
            enableMulticolorLogoContrastFix,
            enableMultiColorLogoToMonochromeConversion,
            convertSolidColorImagesToShapes,
        ],
        500,
    );

    return {
        targetDocumentUrl,
        components,
        priority,
        surfaceSpecificationUrl,
        surfaceSpecification,
        applyOnlyToPlaceholders,
        fillDefaultText,
        smartCrop,
        isBetaEndpoint,
        brandProfileUrl,
        enableMulticolorLogoContrastFix,
        enableMultiColorLogoToMonochromeConversion,
        convertSolidColorImagesToShapes,
        setTargetDocumentUrl,
        setComponents,
        setPriority,
        setSurfaceSpecificationUrl,
        setSurfaceSpecification,
        setApplyOnlyToPlaceholders,
        setSmartCrop,
        setFillDefaultText,
        setIsBetaEndpoint,
        setBrandProfileUrl,
        setEnableMulticolorLogoContrastFix,
        setEnableMultiColorLogoToMonochromeConversion,
        setConvertSolidColorImagesToShapes,
    };
};
